import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { isWeb } from '@/utils/capacitor';
import {
  dataUrlToFile,
  validateImageFile,
  VALID_IMAGE_TYPES,
} from '@/utils/imageUpload';


type ImageSource = 'camera' | 'gallery';

/**
 * Hook for picking images from camera or gallery.
 * Handles both web (file input fallback) and native (Capacitor) platforms.
 */
export function useImagePicker() {
  const pickImageFromWeb = async (source: ImageSource): Promise<File> => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = VALID_IMAGE_TYPES.join(',');

    // For camera source on web, hint to use camera if available
    if (source === 'camera') {
      input.capture = 'environment';
    }

    // Input must be in the DOM for the file picker to open on mobile browsers
    input.style.display = 'none';
    document.body.appendChild(input);

    const filePromise = new Promise<File>((resolve, reject) => {
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        document.body.removeChild(input);
        if (!file) {
          reject(new Error('No file selected'));
          return;
        }

        try {
          validateImageFile(file);
          resolve(file);
        } catch (error) {
          reject(error);
        }
      };

      input.oncancel = () => {
        document.body.removeChild(input);
        reject(new Error('User cancelled'));
      };

      // Auto-reject after 30 seconds if no selection
      setTimeout(() => {
        if (document.body.contains(input)) document.body.removeChild(input);
        reject(new Error('Selection timeout'));
      }, 30000);
    });

    input.click();
    const file = await filePromise;

    // Convert file to data URL then back to File (for consistency with camera flow)
    const reader = new FileReader();
    const dataUrlPromise = new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
    reader.readAsDataURL(file);
    const dataUrl = await dataUrlPromise;

    // Convert data URL to File and return
    return dataUrlToFile(dataUrl, `${source}-${Date.now()}`);
  };

  const pickImageFromNative = async (source: ImageSource): Promise<File> => {
    const cameraSource =
      source === 'camera' ? CameraSource.Camera : CameraSource.Photos;

    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: cameraSource,
    });

    if (!image.dataUrl) {
      throw new Error('No image data returned from camera');
    }

    return dataUrlToFile(image.dataUrl, `${source}-${Date.now()}`);
  };

  const pickImage = async (source: ImageSource): Promise<File> => {
    if (isWeb()) {
      return pickImageFromWeb(source);
    } else {
      return pickImageFromNative(source);
    }
  };

  return { pickImage };
}
